import {Button, Menu} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {useDisclosure} from '@mantine/hooks';
import {IconInfoCircle} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import type {
  ModuleExtensionFormTabRequest,
  ModuleExtensionFormTabResponse,
} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkModuleCustomization} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useCreateFormTabMutation,
  useDeleteFormTabMutation,
  useListFormTabsQuery,
  useUpdateFormTabMutation,
} from 'src/redux/queries/moduleExtension';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useActiveFeatureFlags from 'src/shared/hooks/useActiveFeatureFlags';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {
  ModuleExtensionRegistry,
  moduleExtensionSlug,
} from 'src/shared/constants/moduleExtensionRegistry';
import {FormTabFormModal} from './components/FormTabFormModal';

const ModuleDisplayNames: Record<string, {name: string; module: ModuleEnum}> =
  Object.fromEntries(
    Object.entries(ModuleExtensionRegistry).map(([module, config]) => [
      moduleExtensionSlug(Number(module)),
      {name: config.displayName, module: Number(module)},
    ])
  );

const ModuleCustomizationDetailPage: React.FC = () => {
  const {module: moduleParam} = useParams<{module: string}>();
  const moduleConfig = ModuleDisplayNames[moduleParam ?? ''];
  const module = moduleConfig?.module;
  const moduleName = moduleConfig?.name ?? moduleParam ?? '';

  useSetDocumentTitle(`Module Customization - ${moduleName}`);
  const {setAlert} = useAlert();
  const [modalOpened, modalHandlers] = useDisclosure(false);
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [editingTab, setEditingTab] =
    useState<ModuleExtensionFormTabResponse | null>(null);
  const [deletingTab, setDeletingTab] =
    useState<ModuleExtensionFormTabResponse | null>(null);

  const activeFeatureFlags = useActiveFeatureFlags();
  const hasFormsFeature = activeFeatureFlags?.includes(FeatureFlagEnum.Forms);

  const {hasPermission: hasUpdatePermission} = useHasModulePermission({
    module: ModuleEnum.ModuleExtensions,
    operation: PermissionOperationEnum.Update,
  });

  const canManageFormTabs = hasUpdatePermission && hasFormsFeature;

  const [createFormTab] = useCreateFormTabMutation();
  const [updateFormTab] = useUpdateFormTabMutation();
  const [deleteFormTab] = useDeleteFormTabMutation();

  const columns = useMemo<MRT_ColumnDef<ModuleExtensionFormTabResponse>[]>(
    () => [
      {accessorKey: 'tabLabel', header: 'Label'},
      {
        accessorKey: 'formDefinitionName',
        header: 'Form Definition',
      },
      {accessorKey: 'tabSlug', header: 'Slug'},
      {
        accessorKey: 'sortOrder',
        header: 'Sort Order',
        size: 100,
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        size: 100,
        Cell: ({cell}) => (
          <Pill
            text={cell.getValue<boolean>() ? 'Active' : 'Inactive'}
            color={cell.getValue<boolean>() ? 'green' : 'gray'}
          />
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: `module-customization-${moduleParam}`,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
    defaultSorting: [{id: 'sortOrder', desc: false}],
  });
  const {start, length} = tableState;

  const {data, isLoading, isError, isFetching, refetch} = useListFormTabsQuery(
    {
      module: module ?? ModuleEnum.Invalid,
      start,
      length,
    },
    {refetchOnMountOrArgChange: true, skip: !module}
  );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleCreate = () => {
    setEditingTab(null);
    modalHandlers.open();
  };

  const handleEdit = (tab: ModuleExtensionFormTabResponse) => {
    setEditingTab(tab);
    modalHandlers.open();
  };

  const handleDeleteClick = (tab: ModuleExtensionFormTabResponse) => {
    setDeletingTab(tab);
    confirmDeleteHandlers.open();
  };

  const handleConfirmDelete = async () => {
    if (!deletingTab) return;
    confirmDeleteHandlers.close();
    try {
      await deleteFormTab(deletingTab.id).unwrap();
      setAlert('Form tab deleted.', 'success');
      void refetch();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setDeletingTab(null);
  };

  const handleSave = async (request: ModuleExtensionFormTabRequest) => {
    try {
      if (editingTab) {
        await updateFormTab({...request, id: editingTab.id}).unwrap();
        setAlert('Form tab updated.', 'success');
      } else {
        await createFormTab({...request, module: module!}).unwrap();
        setAlert('Form tab created.', 'success');
      }
      modalHandlers.close();
      void refetch();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActionMenuItems: ({row}) => (
        <>
          {canManageFormTabs && (
            <>
              <Menu.Item onClick={() => handleEdit(row.original)}>
                Edit
              </Menu.Item>
              <Menu.Item
                color="red"
                onClick={() => handleDeleteClick(row.original)}>
                Delete
              </Menu.Item>
            </>
          )}
        </>
      ),
      renderTopToolbarCustomActions: () =>
        canManageFormTabs ? (
          <Button size="xs" onClick={handleCreate}>
            Add Form Tab
          </Button>
        ) : null,
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <BackBar fallbackTo={PathUtility.ModuleCustomization()} />
      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkModuleCustomization.icon}
          subtitle={`Configure extension tabs for ${moduleName}`}>
          {moduleName} Customization
        </PageHeader.Title>
      </PageHeader>
      <Alert
        icon={<IconInfoCircle size={16} />}
        color="blue"
        variant="light"
        mb="md">
        Extension tabs require permissions to be visible. After creating a tab,
        grant access via User Groups module permissions.
      </Alert>
      {table}
      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => {
          confirmDeleteHandlers.close();
          setDeletingTab(null);
        }}
        onConfirm={handleConfirmDelete}
        confirmationButtonColor="red"
        title={`Delete "${deletingTab?.tabLabel}"?`}
        body="This will permanently delete the form tab and remove all associated permission grants. This action cannot be undone."
      />
      <FormTabFormModal
        opened={modalOpened}
        onClose={modalHandlers.close}
        onSave={handleSave}
        editingTab={editingTab}
        module={module}
      />
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.ModuleCustomizationDetail(':module'),
  element: <ModuleCustomizationDetailPage />,
  module: ModuleEnum.ModuleExtensions,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
  allowGlobalAdminBypass: true,
});

export default ModuleCustomizationDetailPage;
